import { useState, useEffect, useMemo } from 'react';
import { subscribeToPlan } from '../../helper/api.helper';
import { getAllCountries, getStatesForCountry } from './geoData';
import { detectLocationFromIP } from '../pricing.service';
import toast from 'react-hot-toast';
import { useAuth } from '../../helper/hooks/useAuth';
import { getUserData } from '../../dashboard/plan/plans.service';

const PlanCard = ({ plan, projectId = null }) => {
  const [selectedOption, setSelectedOption] = useState(0);
  const [country, setCountry] = useState('');
  const [billingState, setBillingState] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const isPaidPlan = Boolean(plan.creditsOptions);
  const countries = useMemo(() => getAllCountries(), []);
  const states = useMemo(() => getStatesForCountry(country), [country]);
  const { userId } = useAuth();
  const [isLocationAlreadySelected, setIsLocationAlreadySelected] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (!userId && !isPaidPlan) return;

        setLocationLoading(true);

        const userData = await getUserData(userId);

        if (userData?.billingCountry) {
          setIsLocationAlreadySelected(true);
          setCountry(userData.billingCountry);
          setBillingState(userData?.billingState || '');
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLocationLoading(false);
      }
    };

    fetchUserData();
  }, [userId]);

  useEffect(() => {
    if (!isPaidPlan || isLocationAlreadySelected) return;

    const fetchLocation = async () => {
      setLocationLoading(true);
      try {
        const { countryCode, stateCode } = await detectLocationFromIP();
        if (countryCode) setCountry(countryCode);
        if (stateCode) setBillingState(stateCode);
      } finally {
        setLocationLoading(false);
      }
    };

    fetchLocation();
  }, [isPaidPlan]);

  const handleSelectChange = (e) => {
    setSelectedOption(parseInt(e.target.value));
  };

  const handleCountryChange = (e) => {
    setCountry(e.target.value);
    setBillingState('');
  };

  const handleFreePlan = async () => {
    setSubscribing(true);
    try {
      const res = await subscribeToPlan({ free: true, ...(projectId && { projectId }) });
      const data = await res.json();
      if (res.ok || data.message === 'User already has a subscription') {
        if (projectId) {
          window.location.href = `/processing/${projectId}`;
        } else {
          window.location.href = plan.buttonLink;
        }
      } else {
        toast.error(data.message || 'Failed to subscribe');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  const handleGetStarted = async () => {
    if (!country) {
      toast.error('Please select your billing country');
      return;
    }
    if (country === 'IN' && !billingState) {
      toast.error('Please select your billing state');
      return;
    }
    setSubscribing(true);
    try {
      const selectedCreditsObj = plan.creditsOptions[selectedOption];

      const res = await subscribeToPlan({
        priceId: process.env.REACT_APP_STRIPE_PRICE_ID,
        credits: selectedCreditsObj?.credits,
        ...(country === 'IN' && { state: billingState }),
        country,
        projectId
      });

      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error(data.message || 'Failed to initiate checkout');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  const currentPrice = plan.creditsOptions?.[selectedOption]?.price || plan.price;
  const currentINRPrice = plan.creditsOptions?.[selectedOption]?.priceINR;
  const currentCredits = plan.creditsOptions?.[selectedOption]?.credits || 1000;
  const isIndia = country === 'IN';

  // Display plan name logic
  const displayName = plan.creditsOptions
    ? isIndia && currentINRPrice
      ? `₹${currentINRPrice.toLocaleString('en-IN')}`
      : `$${currentPrice}`
    : plan.name;

  return (
    <div className="plan-card">
      <div className="plan-header">
        <div className="price-row">
          {plan.pricePrefix && <span className="price-prefix">{plan.pricePrefix}</span>}
          <h2 className="plan-name">{displayName}</h2>
          {(plan.price !== null || currentPrice) && (
            <span className="billing-period">{plan.billingPeriod}</span>
          )}
        </div>
      </div>

      <div className="plan-credits">
        {plan.customizable && <p className="customizable-text">{plan.creditsText}</p>}
        {plan.creditsOptions ? (
          <div className="credits-dropdown">
            <select
              id="credits"
              name="credits"
              onChange={handleSelectChange}
              value={selectedOption}>
              {plan.creditsOptions.map((option, index) => (
                <option key={index} value={index}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        ) : (
          !plan.customizable &&
          plan.creditsText && <p className="credits-text">{plan.creditsText}</p>
        )}
      </div>

      {isPaidPlan && !isLocationAlreadySelected && (
        <div className="billing-location">
          <div className="location-field">
            <label className="location-label" htmlFor={`country-${plan.id}`}>
              Billing Country
            </label>
            <select
              id={`country-${plan.id}`}
              className="location-select"
              value={country}
              onChange={handleCountryChange}
              disabled={locationLoading}>
              <option value="">{locationLoading ? 'Detecting...' : 'Select country'}</option>
              {countries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {country === 'IN' && !isLocationAlreadySelected && (
            <div className="location-field">
              <label className="location-label" htmlFor={`state-${plan.id}`}>
                Billing State
              </label>
              <select
                id={`state-${plan.id}`}
                className="location-select"
                value={billingState}
                onChange={(e) => setBillingState(e.target.value)}>
                <option value="">Select state</option>
                {states.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {isPaidPlan ? (
        <button className="plan-button" disabled={subscribing} onClick={handleGetStarted}>
          {subscribing ? 'Redirecting...' : 'Get Started'}
        </button>
      ) : (
        <button
          className={`plan-button ${plan.isComingSoon ? 'coming-soon' : ''}`}
          disabled={plan.isComingSoon || subscribing}
          onClick={() => {
            if (!plan.isComingSoon) {
              handleFreePlan();
            }
          }}>
          {subscribing ? 'Redirecting...' : plan.buttonText}
        </button>
      )}

      <div className="plan-features">
        {plan.features.map((feature, index) => {
          let dynamicLines = feature.lines;
          if (feature.isDynamic && plan.creditsOptions && feature.lines) {
            dynamicLines = [
              `<span class='highlight'>${currentCredits}</span> Credits`,
              ...feature.lines.slice(1)
            ];
          }

          const formatText = (text) => text || '';

          return (
            <div key={index} className="feature-item">
              <span className="feature-icon">
                <img src={feature.icon} alt="" className="icon" />
              </span>
              <div className="feature-content">
                {dynamicLines ? (
                  dynamicLines.map((line, lineIndex) => (
                    <p key={lineIndex} dangerouslySetInnerHTML={{ __html: formatText(line) }} />
                  ))
                ) : (
                  <p dangerouslySetInnerHTML={{ __html: formatText(feature.text) }} />
                )}
              </div>
            </div>
          );
        })}
      </div>

      <style jsx>{`
        .plan-card {
          background: var(--white);
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 36px 28px;
          width: 100%;
          max-width: 320px;
          min-height: 600px;
          display: flex;
          flex-direction: column;
          transition: all 0.3s ease;
        }

        .plan-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }

        .plan-header {
          margin-bottom: 20px;
        }

        .price-row {
          display: flex;
          align-items: baseline;
          gap: 6px;
          flex-wrap: wrap;
        }

        .price-prefix {
          font-size: 0.8125rem;
          font-weight: 400;
          color: var(--text-color-gray);
          line-height: 1.2;
        }

        .plan-name {
          font-size: 1.875rem;
          font-weight: 700;
          color: var(--text-color-primary);
          margin: 0;
          line-height: 1.2;
        }

        .billing-period {
          font-size: 0.8125rem;
          color: var(--text-color-gray);
          font-weight: 400;
          line-height: 1.2;
        }

        .plan-credits {
          margin-bottom: 16px;
          min-height: 60px;
        }

        .customizable-text {
          font-size: 0.8125rem;
          color: var(--text-color-gray);
          margin: 0 0 8px 0;
          font-weight: 400;
        }

        .credits-text {
          font-size: 0.8125rem;
          font-weight: 700;
          color: var(--btn-purple-color);
          text-transform: uppercase;
          letter-spacing: 0.3px;
          margin: 0;
          line-height: 1.4;
        }

        .credits-dropdown {
          margin-top: 8px;
        }

        .billing-location {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-bottom: 16px;
        }

        .location-field {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .location-label {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--text-color-gray);
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }

        .location-select {
          width: 100%;
          padding: 10px 14px;
          border: 2px solid var(--text-color-primary);
          border-radius: 6px;
          font-size: 0.8125rem;
          font-weight: 500;
          color: var(--text-color-primary);
          background-color: var(--white);
          outline: none;
          transition: border-color 0.2s;
          box-sizing: border-box;
        }

        .location-select {
          padding-right: 36px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 12 12'%3E%3Cpath fill='%233010ff' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          cursor: pointer;
        }

        .location-select:focus {
          border-color: var(--btn-purple-color);
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
        }

        .location-select:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .credits-dropdown select {
          width: 100%;
          padding: 12px 36px 12px 14px;
          border: 2px solid var(--text-color-primary);
          border-radius: 6px;
          font-size: 0.8125rem;
          font-weight: 600;
          color: var(--btn-purple-color);
          background-color: var(--white);
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 12 12'%3E%3Cpath fill='%233010ff' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 15px center;
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          cursor: pointer;
          outline: none;
          transition: border-color 0.2s;
        }

        .credits-dropdown select:hover {
          border-color: var(--btn-purple-color);
        }

        .credits-dropdown select:focus {
          border-color: var(--btn-purple-color);
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
        }

        .plan-button {
          width: 100%;
          padding: 12px 24px;
          border: none;
          border-radius: 6px;
          font-size: 0.9375rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          margin-bottom: 24px;
        }

        .plan-button:not(.coming-soon) {
          background: var(--btn-purple-color);
          color: var(--white);
        }

        .plan-button:not(.coming-soon):hover {
          background: var(--btn-purple-color);
        }

        .plan-button.coming-soon {
          background: var(--white);
          color: var(--btn-purple-color);
          border: 1px solid var(--btn-purple-color);
          cursor: not-allowed;
        }

        .plan-features {
          display: flex;
          flex-direction: column;
          gap: 14px;
          flex: 1;
        }

        .feature-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
        }

        .feature-icon {
          font-size: 1.125rem;
          flex-shrink: 0;
          margin-top: 1px;
          line-height: 1;
        }

        .feature-content {
          flex: 1;
        }

        .feature-content p {
          margin: 0;
          font-size: 0.875rem;
          line-height: 1.5;
          color: var(--text-color-primary);
          font-weight: 500;
        }

        /* Tablet portrait */
        @media (max-width: 768px) {
          .plan-card {
            max-width: 60%;
            min-height: auto;
            padding: 28px 24px;
          }

          .plan-name {
            font-size: 1.625rem;
          }

          .price-row {
            gap: 5px;
          }

          .billing-period,
          .price-prefix {
            font-size: 0.75rem;
          }

          .plan-credits {
            min-height: auto;
          }

          .credits-dropdown select {
            font-size: 0.75rem;
            padding: 9px 32px 9px 12px;
          }

          .feature-content p {
            font-size: 0.8125rem;
          }

          .feature-icon {
            font-size: 1rem;
          }
        }

        /* Mobile */
        @media (max-width: 480px) {
          .plan-card {
            max-width: 80%;
            padding: 24px 20px;
          }

          .plan-header {
            margin-bottom: 16px;
          }

          .plan-name {
            font-size: 1.5rem;
          }

          .billing-period,
          .price-prefix,
          .customizable-text {
            font-size: 0.6875rem;
          }

          .plan-credits {
            margin-bottom: 14px;
          }

          .credits-text {
            font-size: 0.75rem;
          }

          .credits-dropdown select {
            font-size: 0.6875rem;
            padding: 8px 28px 8px 10px;
          }

          .plan-button {
            padding: 10px 18px;
            font-size: 0.8125rem;
            margin-bottom: 20px;
          }

          .plan-features {
            gap: 12px;
          }

          .feature-item {
            gap: 8px;
          }

          .feature-icon {
            font-size: 0.9375rem;
          }

          .feature-content p {
            font-size: 0.75rem;
            line-height: 1.4;
          }
        }
      `}</style>
    </div>
  );
};

export default PlanCard;
